import type { EnvironmentValue } from '../types/EnvironmentValue';
import type { Environment } from '../types/Environment';
import type { AppType } from '../types/AppType';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { RequiredEnvironmentRegistryManager } from '../registry/RequiredEnvironmentRegistryManager';

/**
 * @group Functions
 * @summary <blockquote>Gets the list of required environment variable keys for a specific app type and environment combination.</blockquote>
 * 
 * @param appType - The application type ('web' or 'mobile')
 * @param environment - The environment ('development', 'preview', or 'production')
 * @returns Result containing array of required environment variable keys or error if registry not initialized
 * 
 * @example
 * ```typescript
 * import { getRequiredEnvironmentKeys } from '@shared/utils';
 * 
 * // Get required vars for web production
 * const result = getRequiredEnvironmentKeys('web', 'production');
 * result.match({
 *   ok: (requiredVars) => {
 *     // Check if a variable is required
 *     const isRequired = requiredVars.includes(ENVIRONMENT_VALUES.DOXY.NEXT_PUBLIC_DOXY_URL);
 *   },
 *   err: (error) => console.error(error.errorMessage)
 * });
 * ```
 */
export function getRequiredEnvironmentKeys(
  appType: AppType,
  environment: Environment
): Result<readonly EnvironmentValue[], AppError> {
  const registryResult = RequiredEnvironmentRegistryManager.get();
  if (registryResult.isErr) {
    return err(registryResult.getFail());
  }
  const registry = registryResult.value;
  return ok(registry[appType][environment]);
}

